/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from "react";
import ItemSection from "./ItemSection";
import ItemOption from 'model/character/ItemOption';
import Armor from "model/stats/Armor";
import AttributeValues from "model/stats/AttributeValues";
import ArmorSelector from "components/fields/ArmorSelector";

interface EquipmentSelectorProps {
  equipmentRaceOptions: ItemOption[];
  equipmentRaceSelected: string[];
  setEquipmentRaceSelected: React.Dispatch<React.SetStateAction<string[]>>;
  equipmentClassOptions: ItemOption[];
  equipmentClassSelected: string[];
  setEquipmentClassSelected: React.Dispatch<React.SetStateAction<string[]>>;
  equipmentBackgroundOptions: ItemOption[];
  equipmentBackgroundSelected: string[];
  setEquipmentBackgroundSelected: React.Dispatch<React.SetStateAction<string[]>>;
  armorProficiencies: string[];
  weaponProficiencies: string[];
  attributeValues: AttributeValues;
  equipedArmor: Armor | null;
  equipedArmorClass: number;
  equipedArmorDesc: string;
  armorBlockOpen: boolean;
  setArmorBlockOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdateArmor: (ac: number, desc: string, armor: Armor | null) => void;
  onlyProfWeapons: boolean;
  setOnlyProfWeapons: React.Dispatch<React.SetStateAction<boolean>>;
  profWeaponNames: string[];
  possibleWeaponNames: string[];
  equipedWeapon1: string;
  setEquipedWeapon1: React.Dispatch<React.SetStateAction<string>>;
  equipedWeapon1Conflict: boolean;
  equipedWeapon2: string;
  setEquipedWeapon2: React.Dispatch<React.SetStateAction<string>>;
  equipedWeapon2Conflict: boolean;
  equipedWeapon3: string;
  setEquipedWeapon3: React.Dispatch<React.SetStateAction<string>>;
  equipedWeapon3Conflict: boolean;
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
  equipmentRaceOptions, equipmentRaceSelected, setEquipmentRaceSelected,
  equipmentClassOptions, equipmentClassSelected, setEquipmentClassSelected,
  equipmentBackgroundOptions, equipmentBackgroundSelected, setEquipmentBackgroundSelected,
  armorProficiencies, weaponProficiencies, attributeValues,
  equipedArmor, equipedArmorClass, equipedArmorDesc,
  armorBlockOpen, setArmorBlockOpen, onUpdateArmor,
  onlyProfWeapons, setOnlyProfWeapons, profWeaponNames, possibleWeaponNames,
  equipedWeapon1, setEquipedWeapon1, equipedWeapon1Conflict,
  equipedWeapon2, setEquipedWeapon2, equipedWeapon2Conflict,
  equipedWeapon3, setEquipedWeapon3, equipedWeapon3Conflict,
}) => {
  return (
    <table>
      <tbody>
        <tr>
          <td style={{borderRight: '1px solid', verticalAlign: 'top', padding: '10px'}}>
            <ItemSection racialSelected={equipmentRaceSelected}          racialOptions={equipmentRaceOptions}           onRacialSelectedChanged={(s) => setEquipmentRaceSelected(s)}
                        classSelected={equipmentClassSelected}           classOptions={equipmentClassOptions}           onClassSelectedChanged={(s) => setEquipmentClassSelected(s)}
                        backgroundSelected={equipmentBackgroundSelected} backgroundOptions={equipmentBackgroundOptions} onBackgroundSelectedChanged={(s) => setEquipmentBackgroundSelected(s)}
                        possibleSet={null} name='Starting Equipment' />
          </td>
          <td style={{verticalAlign: 'top', padding: '10px'}}>
            <div style={{fontWeight: '600', textAlign: 'center', padding: '10px'}}>Current Equipment:</div>
            <div style={{fontWeight: '500', textAlign: 'left'}}>Armor:</div>
            <table>
              <tbody>
                <tr>
                  <td style={{fontWeight: '600', fontStyle: 'italic'}}>Profs:</td>
                  <td style={{fontWeight: '300', fontStyle: 'italic'}}>{armorProficiencies.join(", ")}</td>
                </tr>
              </tbody>
            </table>
            Armor:
            &nbsp;
            <span style={{border: 'solid 1px grey', borderRadius: '5px', background: 'white'}}>
              <ArmorSelector armorType={equipedArmor} armorClass={equipedArmorClass} armorDesc={equipedArmorDesc} useMm2024={false}
                             dexterity={attributeValues.dexterity} strength={attributeValues.strength} isOpen={armorBlockOpen}
                             setIsOpen={(newIsOpen) => {setArmorBlockOpen(newIsOpen)}}
                             onSave={onUpdateArmor} />
            </span>
            <br/>
            <br/>
            <hr/>
            <br/>
            <div style={{fontWeight: '500', textAlign: 'left'}}>Weapons:</div>
            <table>
              <tbody>
                <tr>
                  <td style={{fontWeight: '600', fontStyle: 'italic'}}>Profs:</td>
                  <td style={{fontWeight: '300', fontStyle: 'italic'}}>{weaponProficiencies.join(", ")}</td>
                </tr>
              </tbody>
            </table>
            <table>
              <tbody>
                <tr>
                  <td></td>
                  <td style={{fontSize:'smaller', fontStyle: 'italic'}}>
                    <input type="checkbox" id="onlyProfWeapons" name="onlyProfWeapons" checked={onlyProfWeapons} onChange={() => setOnlyProfWeapons((prev) => !prev)}/>
                    <label htmlFor="onlyProfWeapons">Only show weapons in the proficiency set above</label>
                  </td>
                </tr>
                <tr>
                  <td>Primary:</td>
                  <td>
                    <select value={equipedWeapon1}
                        style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                        onChange={(e) => setEquipedWeapon1(e.target.value)}>
                        <option value=''>-- Select --</option>
                        {
                            (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))
                        }
                    </select>
                    {equipedWeapon1Conflict && (<>
                        <br/>
                        <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Secondary:</td>
                  <td>
                    <select value={equipedWeapon2}
                        style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                        onChange={(e) => setEquipedWeapon2(e.target.value)}>
                        <option value=''>None</option>
                        {
                            (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))
                        }
                    </select>
                    {equipedWeapon2Conflict && (<>
                        <br/>
                        <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Alternate:</td>
                  <td>
                    <select value={equipedWeapon3}
                        style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                        onChange={(e) => setEquipedWeapon3(e.target.value)}>
                        <option value=''>None</option>
                        {
                            (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))
                        }
                    </select>
                    {equipedWeapon3Conflict && (<>
                        <br/>
                        <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default EquipmentSelector;
